import { useCallback } from "react";
import { Portal } from "react-portal";
import { toast } from "react-hot-toast";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { DisqualifyModal } from "@/modals/disqualify-modal";
import { EditContactModal } from "@/modals/edit-contact-modal";
import { AccountSendEmailModal } from "@/modals/account-send-email-modal";
import CreateNewLeadModal from "@/modals/create-new-lead-modal";
import { ACCOUNT_STATUSES } from "shared/lib/constants/account";
import AccountCancelMeetingModal from "@/modals/account-cancel-meeting-modal";
import { AccountEditCallbackModal } from "@/modals/account-edit-callback-modal";
import { AccountBookMeetingModal } from "@/modals/account-book-meeting-modal";
import EditAccountModal from "@/modals/edit-account-modal";
import { useAccountsContext } from "@/hooks/use-accounts-context";
import { CreateNewLeadFormI } from "@/modals/create-new-lead-modal/new-lead-form";
import { glencocoClientAPI } from "@/api/glencoco";
import { handleErrorMessage } from "@/helpers/error";
import { DeleteAccountLeadConfirmationModal } from "@/components/modals/delete-account-lead-confirmation-modal";
import { AddUserToListSidebar } from "@/modules/pipeline/shared/add-user-to-list-sidebar";
import { useAddUserToListSidebarContext } from "@/modules/pipeline/shared/add-user-to-list-sidebar/context";
import { SetUnsetPrimaryLeadConfirmationModal } from "@/modals/set-primary-lead-confirmation-modal";
import { SetContactTypeModal } from "@/modals/set-contact-type-modal";
import { EmailTemplatesSidebar } from "@/components/modules/email-templates-sidebar";
import { useEmailTemplatesSidebarContext } from "@/components/modules/email-templates-sidebar/context";
import { useEffectOnce } from "shared/lib/hooks/use-effect-once";
import { useLocalStorage } from "@/context/local-storage";
import { useLiveTransfer } from "@/modules/pipeline/account-details/hooks/use-live-transfer";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { LIVE_TRANSFER_AMB_MODAL_ID } from "@/components/modals/account-live-transfer-modal/after-meeting-booked";
import { DNCNumbersToggleModal } from "@/components/modals/dnc-numbers-toggle-modal";
import {
  useResetCampaignScript,
  useShouldSendFollowUpEmail,
} from "@/modules/pipeline/account-details/data/mutations";
import DangerousActionConfirmationModal from "@/components/modals/dangerous-action-confirmation-modal";
import { FindMoreLeadsModal } from "@/components/modals/account-find-more-leads-modal";
import { useFetchCampaignIcpTitles } from "@/modules/pipeline/account-details/data/queries";
import { useCallingContext } from "@/hooks/dialer/use-dialer-context";
import { dd } from "@/helpers/datadog";
import { LOG_CATEGORIES } from "@/constants/logs";
import { ERROR_CATEGORIES } from "@/constants/errors";
import { useListTourContext } from "@/modules/tours/list/context";

import { swallow } from "@/api/legacy-swallow";

export const ACCOUNT_DETAILS_CREATE_NEW_LEAD_MODAL_ID =
  "account-details-create-new-lead-modal";

export const RESET_CAMPAIGN_SCRIPT_MODAL_ID = "reset-campaign-script-modal";

export const AccountDetailsModals = () => {
  const { clearModalAccount, modalAccount } = useAccountsContext();
  const { setEmailFollowUpData } = useAccountDetailsContext();
  const { mutate: resetCampaignScript } = useResetCampaignScript();
  const { isLiveTransferMode, isLiveTransferIsMeetingBooked } =
    useLiveTransfer();

  const { call } = useCallingContext();

  const storage = useLocalStorage();
  const { isTourOpen } = useListTourContext();

  const {
    focusedContact,
    setFocusedContact,
    campaign,
    account,
    accountStatus,
    contactsForContextMenu,
    onAccountUpdate: handleAccountUpdate,
    accountHistoryData: { reloadData: reloadAccountHistory },
  } = useAccountDetailsContext();

  const {
    isOpen: isAddUserToListSidebarOpen,
    onClose: closeAddUserToListSidebar,
  } = useAddUserToListSidebarContext();

  const {
    isOpen: isEmailTemplatesSidebarOpen,
    onClose: closeEmailTemplatesSidebar,
  } = useEmailTemplatesSidebarContext();

  const { data: campaignIcpTitles } = useFetchCampaignIcpTitles(
    isTourOpen ? undefined : campaign?.id
  );

  const { mutateAsync: shouldSendFollowUpEmailAsync } =
    useShouldSendFollowUpEmail();

  const submitNewLeadToAccountAsBacklog = async (data: CreateNewLeadFormI) => {
    if (campaign?.id && account?.id) {
      const API = glencocoClientAPI();

      const resp = await swallow(
        API.addLeadToAccount(campaign?.id, account?.id, {
          first_name: data.first_name,
          last_name: data.last_name,
          title: data.title,
          phone: data.phone,
          mobile: data.mobile,
          email: data.email,
          linkedin_url: data.linkedin_url,
          // Person location fields only (account already exists)
          person_city: data.person_city,
          person_state: data.person_state,
          person_country: data.person_country,
          person_postal_code: data.person_postal_code,
        })
      ).catch((e) => e);

      if (resp.status === 200) {
        toast.success("Success");
        return true;
      } else {
        handleErrorMessage(
          resp?.response?.data?.error_code as number,
          "Failed to add new lead. Please message us in the chat window on the bottom right so we can assist."
        );
      }
    }
    return false;
  };

  const handleResetCampaignScript = async () => {
    if (campaign?.id) {
      resetCampaignScript(campaign.id);
    }
  };

  // Call this function when we need to both update account data and reload account history
  const onModalActionSuccess = useCallback(() => {
    handleAccountUpdate();
    reloadAccountHistory();

    if (focusedContact) {
      setFocusedContact(undefined);
    }
  }, [handleAccountUpdate, reloadAccountHistory, focusedContact]);

  const onModalBookMeetingSuccess = useCallback(async () => {
    if (isLiveTransferMode && !isLiveTransferIsMeetingBooked) {
      setTimeout(() => {
        modalHelpers.open(LIVE_TRANSFER_AMB_MODAL_ID);
      }, 1000);
    }

    if (call?.customParameters.get("call_id")) {
      const resp = await swallow(
        shouldSendFollowUpEmailAsync({
          campaignId: campaign?.id as string,
          callId: call?.customParameters.get("call_id") as string,
          parameters: {
            has_meeting: true,
          },
        })
      );

      if (resp.status === 200) {
        setEmailFollowUpData?.(resp.data);

        dd.log(
          `${LOG_CATEGORIES.FOLLOW_UP_EMAIL}[BOOKED_MEETING_MODAL] - booked meeting and prepared follow-up`,
          { data: resp.data }
        );
      } else {
        dd.log(
          `${ERROR_CATEGORIES.FOLLOW_UP_EMAIL}[BOOKED_MEETING_MODAL] - failed to prepare follow-up`,
          { data: resp }
        );
      }
    }

    onModalActionSuccess();
  }, [
    onModalActionSuccess,
    isLiveTransferMode,
    isLiveTransferIsMeetingBooked,
    call,
    campaign?.id,
  ]);

  /**
   * Clear persisted data for modals. Right now we save data for AccountSendEmailModal
   * and CreateNewLeadModal
   */
  useEffectOnce(() => {
    storage.emailModalContent = null;
    storage.newLeadModalPersistedValues = null;
  });

  return (
    // @ts-ignore
    <Portal>
      {campaign && account && (
        <DisqualifyModal
          campaignId={campaign?.id}
          account={account}
          contacts={contactsForContextMenu}
          focusedContactId={focusedContact?.id}
          accountStatus={accountStatus}
          onSuccess={onModalActionSuccess}
        />
      )}

      {focusedContact && (
        <>
          <EditContactModal
            campaign={campaign}
            contact={focusedContact}
            onClose={() => setFocusedContact(undefined)}
            onSubmitSuccess={onModalActionSuccess}
          />

          <SetUnsetPrimaryLeadConfirmationModal
            campaign={campaign}
            account={account}
            contact={focusedContact}
            onSuccess={onModalActionSuccess}
          />

          <AccountSendEmailModal
            campaignId={campaign?.id as string}
            contact={focusedContact}
            onSuccess={onModalActionSuccess}
          />

          <DeleteAccountLeadConfirmationModal
            campaignId={campaign?.id}
            contact={focusedContact}
            onSuccess={() => {
              setFocusedContact(undefined);
              onModalActionSuccess();
            }}
          />

          <SetContactTypeModal
            campaignId={campaign?.id}
            accountId={account?.id}
            contactId={focusedContact?.id}
            contactType={focusedContact.contact_disposition_type}
            accountStatus={accountStatus}
            onSuccess={onModalActionSuccess}
          />
        </>
      )}

      {/* THESE MODALS HANDLE NEW ACCOUNT LEAD CREATION */}
      <CreateNewLeadModal
        modalId={ACCOUNT_DETAILS_CREATE_NEW_LEAD_MODAL_ID}
        campaign={campaign}
        account={account}
        handleNewLeadSubmitAsBacklog={submitNewLeadToAccountAsBacklog}
        onSuccess={onModalActionSuccess}
      />

      {/* THESE MODALS HANDLE UPDATING AN EXISTING ACCOUNT  */}
      {!!modalAccount && (
        <>
          {modalAccount.accountStatus ===
            ACCOUNT_STATUSES.MEETING_SCHEDULED && (
            <AccountCancelMeetingModal
              onClose={clearModalAccount}
              onSuccess={onModalActionSuccess}
            />
          )}

          {modalAccount.targetAccountStatus === ACCOUNT_STATUSES.CALLBACK && (
            <AccountEditCallbackModal
              followUpTime={modalAccount.followUpTime}
              onClose={clearModalAccount}
              onSuccess={onModalActionSuccess}
            />
          )}

          {modalAccount.targetAccountStatus ===
            ACCOUNT_STATUSES.MEETING_SCHEDULED && (
            <AccountBookMeetingModal
              onClose={clearModalAccount}
              onSuccess={onModalBookMeetingSuccess}
            />
          )}
        </>
      )}

      <EditAccountModal
        campaign={campaign}
        account={account}
        onSubmitSuccess={onModalActionSuccess}
      />

      <AddUserToListSidebar
        isOpen={isAddUserToListSidebarOpen}
        onClose={closeAddUserToListSidebar}
      />

      <EmailTemplatesSidebar
        isOpen={isEmailTemplatesSidebarOpen}
        onClose={closeEmailTemplatesSidebar}
      />

      <DNCNumbersToggleModal
        campaign={campaign}
        contact={focusedContact}
        onSuccess={onModalActionSuccess}
      />

      <DangerousActionConfirmationModal
        modalId={RESET_CAMPAIGN_SCRIPT_MODAL_ID}
        title="Reset script"
        description="Are you sure you want to reset your custom script back to the default? This action is irreversible."
        callback={handleResetCampaignScript}
      />

      <FindMoreLeadsModal
        onSuccess={handleAccountUpdate}
        defaultTitles={campaignIcpTitles}
      />
    </Portal>
  );
};
